import { expectArray, expectBoolean, expectNullable, expectNumber, expectRecord, expectString } from "./schema";
import { STUDENT_BILLING_CHARGE_TYPES, parseStudentBillingAmount, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
export const STUDENT_BILLING_PAYMENT_EXECUTION_STATUSES = [
    "invoicePending",
    "paymentPending",
    "processing",
    "retryPending",
    "paid",
    "failed",
    "cancelled"
];
export const STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES = [
    "PAYER_ACTION_REQUIRED",
    "PAYMENT_METHOD_DECLINED",
    "PAYMENT_CANCELLED"
];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function nullableString(value, path) {
    return expectNullable(value, (item, itemPath) => expectString(item, itemPath), path);
}
function positiveInteger(value, path) {
    const parsed = expectNumber(value, path);
    if (!Number.isSafeInteger(parsed) || parsed < 1)
        throw new Error(`${path} must be a positive integer`);
    return parsed;
}
export function parseCreateStudentBillingPaymentExecutionRequest(value, path = "createStudentBillingPaymentExecutionRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedBillingUnitVersion"], path);
    return { expectedBillingUnitVersion: positiveInteger(record.expectedBillingUnitVersion, `${path}.expectedBillingUnitVersion`) };
}
export function parseStudentBillingPaymentExecutionDto(value, path = "studentBillingPaymentExecution") {
    const record = expectRecord(value, path);
    exact(record, [
        "executionId", "billingUnitId", "payerId", "chargeType", "serviceMonth", "status", "amount", "currency",
        "scheduledChargeDate", "platformFeePolicyKey", "attemptCount", "attemptedAt",
        "paidAt", "safeFailureCode", "canRetry", "updatedAt"
    ], path);
    const status = expectString(record.status, `${path}.status`);
    if (!STUDENT_BILLING_PAYMENT_EXECUTION_STATUSES.includes(status)) {
        throw new Error(`${path}.status is invalid`);
    }
    if (record.currency !== "JPY")
        throw new Error(`${path}.currency must be JPY`);
    const chargeType = expectString(record.chargeType, `${path}.chargeType`);
    if (!STUDENT_BILLING_CHARGE_TYPES.includes(chargeType)) {
        throw new Error(`${path}.chargeType is invalid`);
    }
    return {
        executionId: parseStudentBillingIdentifier(record.executionId, `${path}.executionId`),
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        chargeType: chargeType,
        serviceMonth: record.serviceMonth === null ? null : parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        status: status,
        amount: parseStudentBillingAmount(record.amount, `${path}.amount`),
        currency: "JPY",
        scheduledChargeDate: parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`),
        platformFeePolicyKey: nullableString(record.platformFeePolicyKey, `${path}.platformFeePolicyKey`),
        attemptCount: positiveInteger(record.attemptCount, `${path}.attemptCount`),
        attemptedAt: nullableString(record.attemptedAt, `${path}.attemptedAt`),
        paidAt: nullableString(record.paidAt, `${path}.paidAt`),
        safeFailureCode: record.safeFailureCode === null
            ? null
            : (() => {
                const code = expectString(record.safeFailureCode, `${path}.safeFailureCode`);
                if (!STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES.includes(code)) {
                    throw new Error(`${path}.safeFailureCode is invalid`);
                }
                return code;
            })(),
        canRetry: expectBoolean(record.canRetry, `${path}.canRetry`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingPayerPaymentStatusDto(value, path = "studentBillingPayerPaymentStatus") {
    const record = expectRecord(value, path);
    exact(record, ["payerId", "items"], path);
    return {
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        items: expectArray(record.items, parseStudentBillingPaymentExecutionDto, `${path}.items`)
    };
}
function chargeMonthForServiceMonth(serviceMonth) {
    const normalized = parseStudentBillingServiceMonth(serviceMonth);
    const year = Number(normalized.slice(0, 4));
    const month = Number(normalized.slice(5, 7));
    return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}
/** Fixed business rule: the following service month is charged on the preceding month's 26th. */
export function studentBillingRegularScheduledChargeDate(serviceMonth) {
    const chargeMonth = chargeMonthForServiceMonth(serviceMonth);
    return `${chargeMonth.year}-${String(chargeMonth.month).padStart(2, "0")}-26`;
}
/** Fixed business rule: changes close at the end of 23:59 JST on the preceding month's 24th. */
export function studentBillingRegularChangeDeadlineAt(serviceMonth) {
    const chargeMonth = chargeMonthForServiceMonth(serviceMonth);
    return new Date(Date.UTC(chargeMonth.year, chargeMonth.month - 1, 24, 14, 59, 59, 999)).toISOString();
}
export function isStudentBillingChargeRetryable(status) {
    return status === "retryPending" || status === "failed";
}
